import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import oturumSaati from '../../utils/oturumSaati';
import { useAuth } from '../../context/AuthContext';

/**
 * Oturumun son 5 dakikası (03.11.2026, kullanıcı isteği): ekranın en üstünde kırmızı, asimetrik "adacık" sayaç +
 * "Oturumunuzun sonlanmasına 5 dakika kaldı" uyarısı. Kalan süre sunucudan gelir (utils/oturumSaati.js); sayaç
 * yalnız gösterir, süreyi sunucu tutar.
 *   bosta: hareketsizlik — "Oturumu uzat" süreyi yeniler (POST /login/uzat).
 *   sure : 24 saatlik üst sınır — uzatılamaz, yalnız haber verilir.
 * Sıfıra inince süreyi uzatmayan /login/durum sorulur: oturum bittiyse 401 gelir, apiAuth kullanıcıyı sebebiyle
 * giriş ekranına götürür; başka sekmede işlem yapıldıysa yeni kalan süre gelir ve sayaç kaybolur.
 */
const ESIK_SN = 5 * 60;

const sureYaz = (sn) => {
  const s = Math.max(0, Math.ceil(sn));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const OturumSayaci = () => {
  const { logout } = useAuth();
  const [durum, setDurum] = useState(oturumSaati.durum);
  const [simdi, setSimdi] = useState(Date.now);
  const [uyariKapali, setUyariKapali] = useState(false);
  const [uzatiliyor, setUzatiliyor] = useState(false);
  const sonSorgu = useRef(0);

  useEffect(() => oturumSaati.abone(() => setDurum(oturumSaati.durum())), []);
  useEffect(() => {
    const t = setInterval(() => setSimdi(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const kalan = durum.bitis ? Math.ceil((durum.bitis - simdi) / 1000) : null;
  const gorunur = kalan !== null && kalan <= ESIK_SN;

  useEffect(() => {
    if (!gorunur) setUyariKapali(false);
  }, [gorunur]);

  useEffect(() => {
    if (kalan === null || kalan > 0 || Date.now() - sonSorgu.current < 3000) return;
    sonSorgu.current = Date.now();
    fetch(`${API_BASE_URL}/login/durum`).catch(() => {});
  }, [kalan]);

  if (!gorunur) return null;

  const uzat = async () => {
    setUzatiliyor(true);
    try {
      await fetch(`${API_BASE_URL}/login/uzat`, { method: 'POST' });
    } catch (e) {
      // Ağ hatası: sayaç sürer, kullanıcı tekrar deneyebilir.
    } finally {
      setUzatiliyor(false);
    }
  };

  const bosta = durum.tur === 'bosta';
  const son = kalan <= 60;

  return (
    <>
      <style>{STIL}</style>
      <div className={`os-ada${son ? ' os-son' : ''}`} role="timer" aria-label={`Oturumun kapanmasına ${sureYaz(kalan)} kaldı`}>
        <span className="os-nokta" aria-hidden />
        <span className="os-etiket">{bosta ? 'Oturum kapanıyor' : 'Günlük oturum bitiyor'}</span>
        <span className="os-sure">{sureYaz(kalan)}</span>
        {bosta && (
          <button type="button" className="os-uzat" onClick={uzat} disabled={uzatiliyor}>
            {uzatiliyor ? '…' : 'Uzat'}
          </button>
        )}
      </div>
      <Modal open={!uyariKapali} onCancel={() => setUyariKapali(true)} footer={null} width={440} centered
        rootClassName="os-modal" maskClosable={false}>
        <div className="os-ikon" aria-hidden><ClockCircleOutlined /></div>
        <h3 className="os-baslik">
          {bosta ? 'Oturumunuzun sonlanmasına 5 dakika kaldı' : 'Günlük oturum süreniz doluyor'}
        </h3>
        <p className="os-metin">
          {bosta
            ? <>Bir süredir işlem yapmadınız. Devam etmek için oturumu uzatın; aksi halde güvenliğiniz için oturumunuz{' '}
              <b className="os-canli">{sureYaz(kalan)}</b> sonra kapatılacak ve kaydedilmemiş değişiklikler kaybolabilir.</>
            : <>Güvenliğiniz için oturumlar 24 saatte bir yenilenir. <b className="os-canli">{sureYaz(kalan)}</b> sonra
              yeniden giriş yapmanız gerekecek; kaydedilmemiş işlerinizi kaydedin.</>}
        </p>
        <div className="os-dugmeler">
          {bosta ? (
            <>
              <Button onClick={logout}>Çıkış yap</Button>
              <Button className="os-birincil" type="primary" loading={uzatiliyor} onClick={uzat}>Oturumu uzat</Button>
            </>
          ) : (
            <Button className="os-birincil" type="primary" onClick={() => setUyariKapali(true)}>Tamam</Button>
          )}
        </div>
      </Modal>
    </>
  );
};

// Asimetrik adacık: üst kenara yapışık; sol köşe küçük, sağ köşe büyük yarıçaplı, iki yanda içbükey "omuz".
const KIRMIZI = '#d1221f';
const STIL = `
.os-ada { position: fixed; top: 0; left: 50%; z-index: 1100; transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 10px; padding: 7px 8px 9px 15px;
  background: ${KIRMIZI}; color: #fff; border-radius: 0 0 24px 9px;
  box-shadow: 0 12px 26px -12px rgba(160, 20, 18, .75); font-size: 13px; font-weight: 600; line-height: 1;
  white-space: nowrap; animation: os-gir .35s cubic-bezier(.2, .9, .3, 1.2) both; }
.os-ada::before, .os-ada::after { content: ''; position: absolute; top: 0; pointer-events: none; }
.os-ada::before { left: -9px; width: 9px; height: 9px;
  background: radial-gradient(circle at 0 100%, transparent 8.5px, ${KIRMIZI} 9px); }
.os-ada::after { right: -18px; width: 18px; height: 18px;
  background: radial-gradient(circle at 100% 100%, transparent 17.5px, ${KIRMIZI} 18px); }
.os-nokta { width: 8px; height: 8px; border-radius: 50%; background: #fff; flex: none;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, .7); animation: os-nabiz 1.6s ease-out infinite; }
.os-son .os-nokta { animation-duration: .8s; }
.os-etiket { opacity: .92; }
.os-sure { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: .02em; min-width: 38px; }
.os-uzat { margin-left: 2px; padding: 5px 11px; border: 0; border-radius: 999px; background: #fff; color: ${KIRMIZI};
  font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
.os-uzat:hover { background: #ffecea; }
.os-uzat:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.os-uzat:disabled { opacity: .7; cursor: default; }
@keyframes os-gir { from { transform: translate(-50%, -110%); } to { transform: translate(-50%, 0); } }
@keyframes os-nabiz { 0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, .7); } 100% { box-shadow: 0 0 0 9px rgba(255, 255, 255, 0); } }
@media (prefers-reduced-motion: reduce) { .os-ada, .os-nokta { animation: none; } }
@media (max-width: 480px) { .os-ada { gap: 8px; padding-left: 12px; font-size: 12px; } .os-etiket { display: none; } }
@media print { .os-ada { display: none; } }
.os-modal .ant-modal-content { border-radius: 18px; padding: 26px 26px 22px; }
.os-modal .os-ikon { width: 46px; height: 46px; display: grid; place-items: center; margin-bottom: 14px; border-radius: 13px;
  background: #fef3f2; color: ${KIRMIZI}; font-size: 22px; }
body[data-theme='dark'] .os-modal .os-ikon { background: #2a1214; color: #fda29b; }
.os-modal .os-baslik { margin: 0 0 8px; font-size: 18px; font-weight: 700; line-height: 1.35; }
.os-modal .os-metin { margin: 0; font-size: 14.5px; line-height: 1.6; opacity: .85; }
.os-modal .os-canli { font-variant-numeric: tabular-nums; color: ${KIRMIZI}; }
body[data-theme='dark'] .os-modal .os-canli { color: #fda29b; }
.os-modal .os-dugmeler { display: flex; justify-content: flex-end; gap: 8px; margin-top: 22px; flex-wrap: wrap; }
.os-modal .ant-btn { height: 40px; border-radius: 10px; font-weight: 500; }
body .os-modal .ant-btn.os-birincil { background: #1a375c !important; border-color: #1a375c !important; color: #fff !important; }
body .os-modal .ant-btn.os-birincil:hover { background: #23497a !important; border-color: #23497a !important; }
`;

export default OturumSayaci;
